<!--
    Detail view of a provision run (04-frontend.md §5, route /#/cp/dashboard/run/{provisionRunId}).

    Build shape after the repo original bm-billing-run-details.component.html: run header card,
    then a p-tabs band with the "Übersicht" tab (step bar) and EIGHT entity tabs (04 §5).

    WORKBENCH RULES (PAT §K / skill fe_core_review-workbench) — apply in EVERY one of the eight tabs:
      * exclusively the SHARED component <mvs-review-workbench>, never an own p-splitter,
      * exactly ONE #workbenchList instantiation site per tab (the table widget is created ONCE
        and reloaded in place, never remounted),
      * pane visibility ONLY via [panes] — never an @if around the workbench; only the detail
        pane's CONTENT is conditional,
      * autoFitViewport per tab + its own scroll per pane (.cp-run-pane).

    NO .grid/.col-12 around a workbench: it measures its own height from its own top edge to the
    viewport end, but a surrounding column's padding sits BELOW that and pushes the page exactly
    that amount past the viewport (a second scrollbar).

    All icons are Font Awesome (fa-solid), never PrimeIcons — house standard UX7
    (fe_core_ux-guidelines); the same icon family as in model/provision-run-status-presentation.ts.
-->
@if (initialized) {

    <div class="cp-run-detail flex flex-column">

        <!-- Header card of the run: name, provision type, period, valuta, and status. -->
        <div class="surface-card border-round p-3 mb-3">
            @if (provisionRunDto) {
                <div class="flex align-items-center justify-content-between flex-wrap gap-3">
                    <div class="flex flex-column">
                        <span class="text-xl font-semibold text-900">{{ provisionRunDto.name }}</span>
                        <span class="text-600 text-sm">
                            {{ provisionRunDto.provisionTypeDtoName }}
                            &middot;
                            {{ provisionRunDto.periodStartDate | date:'dd.MM.yyyy' }} –
                            {{ provisionRunDto.periodEndDate | date:'dd.MM.yyyy' }}
                            &middot;
                            Valuta {{ provisionRunDto.valutaDate | date:'dd.MM.yyyy' }}
                        </span>
                    </div>

                    <div class="flex align-items-center gap-2">
                        @if (isProcessing(provisionRunDto.status)) {
                            <!-- A transient IN_* status is a snapshot of something still in
                                 progress — hence a spinner instead of a static tag. -->
                            <div class="flex align-items-center gap-2 px-3 py-2 border-round bg-orange-100">
                                <i class="fa-solid fa-spinner fa-spin text-orange-700"></i>
                                <span class="font-semibold text-orange-700">{{ getStatusLabel(provisionRunDto.status) }}</span>
                            </div>
                        } @else {
                            <p-tag [value]="getStatusLabel(provisionRunDto.status)"
                                   [severity]="getStatusSeverity(provisionRunDto.status)"
                                   [rounded]="true">
                            </p-tag>
                        }
                    </div>
                </div>
            } @else {
                <p-skeleton height="3rem"></p-skeleton>
            }
        </div>

        <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">

            <p-tablist>
                <p-tab [value]="'overview'">
                    <i class="fa-solid fa-list-check mr-2"></i>
                    <span>Übersicht</span>
                </p-tab>

                <p-tab [value]="'contractRuns'">
                    <i class="fa-solid fa-file-contract mr-2"></i>
                    <span>Verträge</span>
                    @if (counts.contractRuns !== null) {
                        <p-badge [value]="counts.contractRuns.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>

                <p-tab [value]="'provisionContracts'">
                    <i class="fa-solid fa-calendar-day mr-2"></i>
                    <span>Ereignisse</span>
                    @if (counts.provisionContracts !== null) {
                        <p-badge [value]="counts.provisionContracts.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>

                <p-tab [value]="'contractAgents'">
                    <i class="fa-solid fa-percent mr-2"></i>
                    <span>Sätze</span>
                    @if (counts.contractAgents !== null) {
                        <p-badge [value]="counts.contractAgents.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>

                <p-tab [value]="'messages'">
                    <i class="fa-solid fa-comment mr-2"></i>
                    <span>Meldungen</span>
                    @if (counts.messages !== null) {
                        <p-badge [value]="counts.messages.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>

                <p-tab [value]="'runMessages'">
                    <i class="fa-solid fa-circle-exclamation mr-2"></i>
                    <span>Lauf-Meldungen</span>
                    @if (counts.runMessages !== null) {
                        <p-badge [value]="counts.runMessages.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>

                <p-tab [value]="'contractTransactions'">
                    <i class="fa-solid fa-book mr-2"></i>
                    <span>Vertrags-Transaktionen</span>
                    @if (counts.contractTransactions !== null) {
                        <p-badge [value]="counts.contractTransactions.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>

                <p-tab [value]="'agentTransactions'">
                    <i class="fa-solid fa-wallet mr-2"></i>
                    <span>Konto-Transaktionen</span>
                    @if (counts.agentTransactions !== null) {
                        <p-badge [value]="counts.agentTransactions.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>

                <p-tab [value]="'reserveCoverage'">
                    <i class="fa-solid fa-shield-halved mr-2"></i>
                    <span>Deckung</span>
                    @if (counts.reserveCoverage !== null) {
                        <p-badge [value]="counts.reserveCoverage.toString()" severity="secondary" styleClass="ml-2"></p-badge>
                    }
                </p-tab>
            </p-tablist>

            <p-tabpanels>

                <!-- ── Übersicht: step bar (04 §5.1) — the page's entry tab.

                     [runExecutionAllowed] is DELIBERATELY not bound: this page does not know the
                     cp_runExecution right. The unbound input therefore stays null ("host doesn't
                     know"), and the step bar resolves the right itself — optimistic by default,
                     demoted on a 403 from the server; see its class header. -->
                <p-tabpanel [value]="'overview'">
                    @if (provisionRunDto) {
                        <cp-provision-run-status-flow
                                [provisionRunDto]="provisionRunDto"
                                (provisionRunDtoChange)="handleProvisionRunDtoChange($event)">
                        </cp-provision-run-status-flow>
                    }
                </p-tabpanel>

                <!--
                    ── Verträge: cp.ProvisionContractRun, filtered on the run.

                    The workbench is mounted from page build onward and stays that way — only its
                    CONTENT is conditional. The table widget is created only on the FIRST opening
                    of the tab (ensureTabWidget) and is never replaced afterwards: before that, the
                    @if (contractRunsWidget) renders nothing and nothing is queried; afterwards the
                    same WidgetData reference persists, i.e. the widget is created exactly ONCE
                    (R2). An @if around the panel would break both — see the TS header.
                -->
                <p-tabpanel [value]="'contractRuns'">
                    <mvs-review-workbench [panes]="selection.contractRuns ? 2 : 1"
                                          [panelSizes]="[62, 38]"
                                          [minSizes]="[30, 25]"
                                          [viewportOffsetPx]="workbenchViewportOffsetPx"
                                          [autoFitViewport]="activeTab === 'contractRuns'"
                                          [viewportBottomGapRem]="2">

                        <ng-template #workbenchList>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                <!-- Scope switch "nur Fehler" (FE-A item 7) — a query change, so the
                                     widget is rebuilt by onContractRunFilterChange(), never on a row click. -->
                                <div class="flex align-items-center justify-content-end mb-2">
                                    <p-selectButton [options]="contractRunFilterOptions"
                                                    [ngModel]="contractRunFilter"
                                                    (ngModelChange)="onContractRunFilterChange($event)"
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    [allowEmpty]="false"
                                                    size="small"
                                                    aria-label="Verträge nach Ergebnis filtern">
                                    </p-selectButton>
                                </div>
                                @if (contractRunsWidget) {
                                    <mvs-widget #contractRunsWidgetRef
                                                [widgetData]="contractRunsWidget"
                                                (onObjectSelect)="handleTabSelect('contractRuns', $event)">
                                    </mvs-widget>
                                }
                            </div>
                        </ng-template>

                        <!-- READ-ONLY review of the contract in this run: outcome, Fehlergrund with
                             "Was tun?", Hauptvermittler, bookings + sum. No form, no save, no delete. -->
                        <ng-template #workbenchDetail>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (selection.contractRuns) {
                                    <div class="flex justify-content-end mb-2">
                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary" [text]="true" [rounded]="true"
                                                  pTooltip="Auswahl aufheben" tooltipPosition="left"
                                                  (onClick)="clearTabSelection('contractRuns')">
                                        </p-button>
                                    </div>
                                    <cp-run-detail-contract-run [contractRunId]="selection.contractRuns">
                                    </cp-run-detail-contract-run>
                                }
                            </div>
                        </ng-template>

                    </mvs-review-workbench>
                </p-tabpanel>

                <!--
                    ── Ereignisse (04 §5, tab 2): cp.ProvisionContract, the event time slices in
                    scope for this run. Same workbench contract as every other tab; the widget is
                    built on first opening, but only once the run DTO is there — the run scope is
                    derived from the run's provision type, not from a run FK (see runScopeFilters()
                    in the TS). It is NOT limited to the run's period: the selection has no upper
                    date (BR-023), so events starting after the period end are evaluated and booked
                    too; their valuta is the event start, and only bookings with valuta up to the
                    run's valuta date are paid out by this run. Until then the @if renders nothing,
                    exactly as for a tab that has simply not been opened yet.
                -->
                <p-tabpanel [value]="'provisionContracts'">
                    <mvs-review-workbench [panes]="selection.provisionContracts ? 2 : 1"
                                          [panelSizes]="[62, 38]"
                                          [minSizes]="[30, 25]"
                                          [viewportOffsetPx]="workbenchViewportOffsetPx"
                                          [autoFitViewport]="activeTab === 'provisionContracts'"
                                          [viewportBottomGapRem]="2">

                        <ng-template #workbenchList>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (provisionContractsWidget) {
                                    <mvs-widget #provisionContractsWidgetRef
                                                [widgetData]="provisionContractsWidget"
                                                (onObjectSelect)="handleTabSelect('provisionContracts', $event)">
                                    </mvs-widget>
                                }
                            </div>
                        </ng-template>

                        <ng-template #workbenchDetail>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (selection.provisionContracts) {
                                    <div class="flex align-items-center justify-content-end gap-2 mb-2 flex-wrap">
                                        <!--
                                          "Berechnete Vermittler neu ermitteln" (§3.6, FD-7) — scope is
                                          exactly THIS one event row, which is why the button sits on
                                          the detail side and not above the table. It only opens the
                                          dialog; the dialog loads the preview, demands the mandatory
                                          reason and is the only place that writes. The button stays
                                          visible after a 403 and is disabled with the German reason.
                                        -->
                                        <cp-run-detail-contract-link
                                                [objectType]="'cp.ProvisionContract'"
                                                [objectId]="selection.provisionContracts">
                                        </cp-run-detail-contract-link>

                                        <p-button label="Berechnete Vermittler neu ermitteln"
                                                  icon="fa-regular fa-arrows-rotate"
                                                  severity="secondary"
                                                  [outlined]="true"
                                                  size="small"
                                                  [disabled]="!canAgentChange"
                                                  [pTooltip]="canAgentChange
                                                      ? 'Vorschau öffnen und diese Ereigniszeile bei Bedarf zur Neuermittlung freigeben — die Freigabe ergänzt nur, bestehende Sätze bleiben unverändert.'
                                                      : agentChangeDeniedMessage"
                                                  tooltipPosition="top"
                                                  (onClick)="openCalculatedAgentReset()">
                                        </p-button>

                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary" [text]="true" [rounded]="true"
                                                  pTooltip="Auswahl aufheben" tooltipPosition="left"
                                                  (onClick)="clearTabSelection('provisionContracts')">
                                        </p-button>
                                    </div>
                                    <mvs-object [objectType]="'cp.ProvisionContract'"
                                                [objectId]="selection.provisionContracts">
                                    </mvs-object>

                                    <cp-provision-calculated-agent-reset-dialog
                                            [(visible)]="calculatedAgentResetVisible"
                                            [provisionContractId]="selection.provisionContracts"
                                            [provisionContractLabel]="'Ereigniszeile #' + selection.provisionContracts"
                                            [canAgentChange]="canAgentChange"
                                            (onReset)="handleCalculatedAgentReset()"
                                            (onForbidden)="handleCalculatedAgentResetForbidden()">
                                    </cp-provision-calculated-agent-reset-dialog>
                                }
                            </div>
                        </ng-template>

                    </mvs-review-workbench>
                </p-tabpanel>

                <!-- ── Sätze (04 §5, tab 3): cp.ProvisionContractAgent — one row per
                     Ereignis × Provisionsart × Berater, the level the money is booked on. -->
                <p-tabpanel [value]="'contractAgents'">
                    <mvs-review-workbench [panes]="selection.contractAgents ? 2 : 1"
                                          [panelSizes]="[62, 38]"
                                          [minSizes]="[30, 25]"
                                          [viewportOffsetPx]="workbenchViewportOffsetPx"
                                          [autoFitViewport]="activeTab === 'contractAgents'"
                                          [viewportBottomGapRem]="2">

                        <ng-template #workbenchList>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (contractAgentsWidget) {
                                    <mvs-widget #contractAgentsWidgetRef
                                                [widgetData]="contractAgentsWidget"
                                                (onObjectSelect)="handleTabSelect('contractAgents', $event)">
                                    </mvs-widget>
                                }
                            </div>
                        </ng-template>

                        <ng-template #workbenchDetail>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (selection.contractAgents) {
                                    <!-- "Vertrag öffnen" navigates to the cm contract page - the
                                         read-only way to the contract from this pane. -->
                                    <div class="flex align-items-center justify-content-end gap-2 mb-2">
                                        <cp-run-detail-contract-link
                                                [objectType]="'cp.ProvisionContractAgent'"
                                                [objectId]="selection.contractAgents">
                                        </cp-run-detail-contract-link>
                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary" [text]="true" [rounded]="true"
                                                  pTooltip="Auswahl aufheben" tooltipPosition="left"
                                                  (onClick)="clearTabSelection('contractAgents')">
                                        </p-button>
                                    </div>
                                    <mvs-object [objectType]="'cp.ProvisionContractAgent'"
                                                [objectId]="selection.contractAgents">
                                    </mvs-object>
                                }
                            </div>
                        </ng-template>

                    </mvs-review-workbench>
                </p-tabpanel>

                <!-- ── Meldungen: cp.ProvisionContractRunMessage via the join on ProvisionContractRun. -->
                <p-tabpanel [value]="'messages'">
                    <mvs-review-workbench [panes]="selection.messages ? 2 : 1"
                                          [panelSizes]="[62, 38]"
                                          [minSizes]="[30, 25]"
                                          [viewportOffsetPx]="workbenchViewportOffsetPx"
                                          [autoFitViewport]="activeTab === 'messages'"
                                          [viewportBottomGapRem]="2">

                        <ng-template #workbenchList>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                <!-- Severity scope — errors first is already the sort order. -->
                                <div class="flex align-items-center justify-content-end mb-2">
                                    <p-selectButton [options]="messageFilterOptions"
                                                    [ngModel]="messageFilter"
                                                    (ngModelChange)="onMessageFilterChange($event)"
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    [allowEmpty]="false"
                                                    size="small"
                                                    aria-label="Meldungen nach Schweregrad filtern">
                                    </p-selectButton>
                                </div>
                                @if (messagesWidget) {
                                    <mvs-widget #messagesWidgetRef
                                                [widgetData]="messagesWidget"
                                                (onObjectSelect)="handleTabSelect('messages', $event)">
                                    </mvs-widget>
                                }
                            </div>
                        </ng-template>

                        <!-- READ-ONLY message review: "Was tun?", detail, contract + agent links.
                             Replaces the editable generic form (and its failing template request). -->
                        <ng-template #workbenchDetail>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (selection.messages) {
                                    <div class="flex justify-content-end mb-2">
                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary" [text]="true" [rounded]="true"
                                                  pTooltip="Auswahl aufheben" tooltipPosition="left"
                                                  (onClick)="clearTabSelection('messages')">
                                        </p-button>
                                    </div>
                                    <cp-run-detail-message [messageId]="selection.messages"
                                                           [messageKind]="'contract'">
                                    </cp-run-detail-message>
                                }
                            </div>
                        </ng-template>

                    </mvs-review-workbench>
                </p-tabpanel>

                <!-- ── Lauf-Meldungen: cp.ProvisionRunMessage (payout brake, start validation). -->
                <p-tabpanel [value]="'runMessages'">
                    <mvs-review-workbench [panes]="selection.runMessages ? 2 : 1"
                                          [panelSizes]="[62, 38]"
                                          [minSizes]="[30, 25]"
                                          [viewportOffsetPx]="workbenchViewportOffsetPx"
                                          [autoFitViewport]="activeTab === 'runMessages'"
                                          [viewportBottomGapRem]="2">

                        <ng-template #workbenchList>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (runMessagesWidget) {
                                    <mvs-widget #runMessagesWidgetRef
                                                [widgetData]="runMessagesWidget"
                                                (onObjectSelect)="handleTabSelect('runMessages', $event)">
                                    </mvs-widget>
                                }
                            </div>
                        </ng-template>

                        <ng-template #workbenchDetail>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (selection.runMessages) {
                                    <div class="flex justify-content-end mb-2">
                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary" [text]="true" [rounded]="true"
                                                  pTooltip="Auswahl aufheben" tooltipPosition="left"
                                                  (onClick)="clearTabSelection('runMessages')">
                                        </p-button>
                                    </div>
                                    <!-- READ-ONLY, same pane as the contract messages. -->
                                    <cp-run-detail-message [messageId]="selection.runMessages"
                                                           [messageKind]="'run'">
                                    </cp-run-detail-message>
                                }
                            </div>
                        </ng-template>

                    </mvs-review-workbench>
                </p-tabpanel>

                <!-- ── Vertrags-Transaktionen: cp.ProvisionContractTransaction of the run. -->
                <p-tabpanel [value]="'contractTransactions'">
                    <mvs-review-workbench [panes]="selection.contractTransactions ? 2 : 1"
                                          [panelSizes]="[62, 38]"
                                          [minSizes]="[30, 25]"
                                          [viewportOffsetPx]="workbenchViewportOffsetPx"
                                          [autoFitViewport]="activeTab === 'contractTransactions'"
                                          [viewportBottomGapRem]="2">

                        <ng-template #workbenchList>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                <!-- Summenzeile + Summe je Berater (FE-A item 5) -->
                                @if (provisionRunId) {
                                    <cp-run-detail-totals [provisionRunId]="provisionRunId"
                                                          [kind]="'contract'"
                                                          [reloadToken]="totalsReloadToken">
                                    </cp-run-detail-totals>
                                }
                                @if (contractTransactionsWidget) {
                                    <mvs-widget #contractTransactionsWidgetRef
                                                [widgetData]="contractTransactionsWidget"
                                                (onObjectSelect)="handleTabSelect('contractTransactions', $event)">
                                    </mvs-widget>
                                }
                            </div>
                        </ng-template>

                        <ng-template #workbenchDetail>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (selection.contractTransactions) {
                                    <div class="flex justify-content-end mb-2">
                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary" [text]="true" [rounded]="true"
                                                  pTooltip="Auswahl aufheben" tooltipPosition="left"
                                                  (onClick)="clearTabSelection('contractTransactions')">
                                        </p-button>
                                    </div>
                                    <!-- READ-ONLY: the generic form in readonly mode without its action bar —
                                         no save, no delete, no editable status. -->
                                    <mvs-crud-object [objectType]="'cp.ProvisionContractTransaction'"
                                                     [objectId]="selection.contractTransactions"
                                                     [readonly]="true"
                                                     [removeDelete]="true"
                                                     [hideActionBar]="true">
                                    </mvs-crud-object>
                                }
                            </div>
                        </ng-template>

                    </mvs-review-workbench>
                </p-tabpanel>

                <!-- ── Konto-Transaktionen: cp.ProvisionAgentTransaction of the run. -->
                <p-tabpanel [value]="'agentTransactions'">
                    <mvs-review-workbench [panes]="selection.agentTransactions ? 2 : 1"
                                          [panelSizes]="[62, 38]"
                                          [minSizes]="[30, 25]"
                                          [viewportOffsetPx]="workbenchViewportOffsetPx"
                                          [autoFitViewport]="activeTab === 'agentTransactions'"
                                          [viewportBottomGapRem]="2">

                        <ng-template #workbenchList>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                <!-- Summenzeile + Summe je Berater (FE-A item 5) -->
                                @if (provisionRunId) {
                                    <!-- runStatus drives the "Abrechnung" column of "Summe je Berater"
                                         (draft in CALCULATED/IN_REVIEW/ACTIVATED, final in PAID_OUT/COMPLETED). -->
                                    <cp-run-detail-totals [provisionRunId]="provisionRunId"
                                                          [kind]="'agent'"
                                                          [runStatus]="provisionRunDto?.status"
                                                          [reloadToken]="totalsReloadToken">
                                    </cp-run-detail-totals>
                                }
                                @if (agentTransactionsWidget) {
                                    <mvs-widget #agentTransactionsWidgetRef
                                                [widgetData]="agentTransactionsWidget"
                                                (onObjectSelect)="handleTabSelect('agentTransactions', $event)">
                                    </mvs-widget>
                                }
                            </div>
                        </ng-template>

                        <ng-template #workbenchDetail>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (selection.agentTransactions) {
                                    <div class="flex justify-content-end mb-2">
                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary" [text]="true" [rounded]="true"
                                                  pTooltip="Auswahl aufheben" tooltipPosition="left"
                                                  (onClick)="clearTabSelection('agentTransactions')">
                                        </p-button>
                                    </div>
                                    <!-- READ-ONLY: the generic form in readonly mode without its action bar —
                                         no save, no delete, no editable status. -->
                                    <mvs-crud-object [objectType]="'cp.ProvisionAgentTransaction'"
                                                     [objectId]="selection.agentTransactions"
                                                     [readonly]="true"
                                                     [removeDelete]="true"
                                                     [hideActionBar]="true">
                                    </mvs-crud-object>
                                }
                            </div>
                        </ng-template>

                    </mvs-review-workbench>
                </p-tabpanel>

                <!-- ── Deckung: cp.ProvisionAgentReserveCoverage incl. shortfallAmount (U-03). -->
                <p-tabpanel [value]="'reserveCoverage'">
                    <mvs-review-workbench [panes]="selection.reserveCoverage ? 2 : 1"
                                          [panelSizes]="[62, 38]"
                                          [minSizes]="[30, 25]"
                                          [viewportOffsetPx]="workbenchViewportOffsetPx"
                                          [autoFitViewport]="activeTab === 'reserveCoverage'"
                                          [viewportBottomGapRem]="2">

                        <ng-template #workbenchList>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (reserveCoverageWidget) {
                                    <mvs-widget #reserveCoverageWidgetRef
                                                [widgetData]="reserveCoverageWidget"
                                                (onObjectSelect)="handleTabSelect('reserveCoverage', $event)">
                                    </mvs-widget>
                                }
                            </div>
                        </ng-template>

                        <ng-template #workbenchDetail>
                            <div class="cp-run-pane surface-card border-round-lg p-3">
                                @if (selection.reserveCoverage) {
                                    <div class="flex justify-content-end mb-2">
                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary" [text]="true" [rounded]="true"
                                                  pTooltip="Auswahl aufheben" tooltipPosition="left"
                                                  (onClick)="clearTabSelection('reserveCoverage')">
                                        </p-button>
                                    </div>
                                    <!-- READ-ONLY: the generic form in readonly mode without its action bar —
                                         no save, no delete, no editable status. -->
                                    <mvs-crud-object [objectType]="'cp.ProvisionAgentReserveCoverage'"
                                                     [objectId]="selection.reserveCoverage"
                                                     [readonly]="true"
                                                     [removeDelete]="true"
                                                     [hideActionBar]="true">
                                    </mvs-crud-object>
                                }
                            </div>
                        </ng-template>

                    </mvs-review-workbench>
                </p-tabpanel>

            </p-tabpanels>
        </p-tabs>
    </div>
}
